import { useState } from 'react';
import Button from '../../../shared/components/Button';
import { Select } from '../../../shared/components/Select';
import { formatMinor } from '../../../shared/utils/money';
import { useReferralRewards, useUpdateRewardStatus } from '../hooks/useReferrals';
import {
  LEDGER_STATUS_CLASSES,
  LEDGER_STATUS_LABELS,
  REWARD_TYPE_LABELS,
  RewardLedgerStatus,
  RewardType,
  TRIGGER_LABELS,
  type ReferralRewardLedgerEntry,
} from '../types';

const ALL = '';

const formatDate = (iso: string) =>
  new Date(iso).toLocaleDateString(undefined, { dateStyle: 'medium' });

const amountOf = (entry: ReferralRewardLedgerEntry) => {
  if (entry.resolvedAmountMinor != null) return formatMinor(entry.resolvedAmountMinor);
  // A percentage reward recorded without a base deal amount can't be priced yet.
  if (entry.configuredPercentage != null) return `${entry.configuredPercentage}% (no deal amount)`;
  return '—';
};

/**
 * The reward ledger. One entry per qualifying conversion, written automatically when a referred
 * contact signs or first pays (osprey-api's sales-invoice and invoice hooks).
 *
 * Status moves strictly PENDING → APPROVED → PAID. Cash commission is "Mark paid" once TCS
 * finance has paid it. Discount and store credit are applied as a credit on the referred deal's
 * unsigned sales invoice at trigger time — the issuance note says whether that worked or needs
 * doing by hand — so their last step reads "Mark issued". Either way the action RECORDS what
 * finance did; it moves no money.
 *
 * Filterable by status (server-side). Not by referrer: an entry carries only its attributionId and
 * osprey-api has no attribution list route to map that back to a referrer.
 */
export function LedgerPanel({ canManage }: { canManage: boolean }) {
  const [status, setStatus] = useState(ALL);
  const { data: entries = [], isLoading, error } = useReferralRewards(
    (status || undefined) as RewardLedgerStatus | undefined,
  );
  const updateStatus = useUpdateRewardStatus();

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-end gap-3">
        <Select
          label="Status"
          size="small"
          className="w-44"
          options={[
            { value: ALL, title: 'All statuses' },
            ...Object.entries(LEDGER_STATUS_LABELS).map(([value, title]) => ({ value, title })),
          ]}
          value={status}
          onChange={setStatus}
        />
      </div>

      {error && <p className="text-sm text-red-500">Could not load the reward ledger.</p>}
      {isLoading && <p className="text-sm text-muted-foreground">Loading…</p>}
      {!isLoading && !error && entries.length === 0 && (
        <p className="text-sm text-muted-foreground">
          No rewards {status ? 'with this status' : 'recorded yet'}. Entries appear when a referred
          contact signs or makes their first payment.
        </p>
      )}

      {entries.length > 0 && (
        <div className="bg-card rounded-lg border border-border overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-border bg-muted">
                  <th className="text-left px-4 py-3 font-semibold text-foreground">Triggered</th>
                  <th className="text-left px-4 py-3 font-semibold text-foreground">Reward</th>
                  <th className="text-left px-4 py-3 font-semibold text-foreground">Amount</th>
                  <th className="text-left px-4 py-3 font-semibold text-foreground">Trigger</th>
                  <th className="text-left px-4 py-3 font-semibold text-foreground">Status</th>
                  {canManage && <th className="px-4 py-3" />}
                </tr>
              </thead>
              <tbody>
                {entries.map((entry) => (
                  <tr key={entry.id} className="border-b border-border align-top">
                    <td className="px-4 py-3 text-foreground">{formatDate(entry.triggeredAt)}</td>
                    <td className="px-4 py-3 text-foreground">
                      {REWARD_TYPE_LABELS[entry.rewardType]}
                      {entry.issuanceNote && (
                        <span className="block text-xs text-muted-foreground max-w-[320px]">
                          {entry.issuanceNote}
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-foreground">{amountOf(entry)}</td>
                    <td className="px-4 py-3 text-foreground">
                      {TRIGGER_LABELS[entry.triggerCondition]}
                    </td>
                    <td className="px-4 py-3">
                      <span
                        className={`text-xs px-2 py-0.5 rounded-full ${LEDGER_STATUS_CLASSES[entry.status]}`}
                      >
                        {LEDGER_STATUS_LABELS[entry.status]}
                      </span>
                      {entry.paidAt && (
                        <span className="block text-xs text-muted-foreground mt-1">
                          {formatDate(entry.paidAt)}
                        </span>
                      )}
                    </td>
                    {canManage && (
                      <td className="px-4 py-3 text-right">
                        {entry.status === RewardLedgerStatus.PENDING && (
                          <Button
                            variant="outline"
                            size="sm"
                            className="h-[40px]"
                            disabled={updateStatus.isPending}
                            onClick={() =>
                              updateStatus.mutate({
                                id: entry.id,
                                status: RewardLedgerStatus.APPROVED,
                              })
                            }
                          >
                            Approve
                          </Button>
                        )}
                        {entry.status === RewardLedgerStatus.APPROVED && (
                          <Button
                            size="sm"
                            className="h-[40px]"
                            disabled={updateStatus.isPending}
                            onClick={() =>
                              updateStatus.mutate({ id: entry.id, status: RewardLedgerStatus.PAID })
                            }
                          >
                            {entry.rewardType === RewardType.CASH_COMMISSION ? 'Mark paid' : 'Mark issued'}
                          </Button>
                        )}
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
